<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row :straightTitle="$i18n.t('sidebar.prints.prints')" class="pt-5"> </card-title-row>
      <sidebar-form :inputs="inputs" v-model="printParams" ref="form" :is-editing-on="!isStickyButtonsLoading">
        <template #printType="scope">
          <v-radio-group class="my-0 py-0" v-model="printType" dense hide-details :disabled="isStickyButtonsLoading">
            <template v-for="(item, idx) of scope.items">
              <v-col :key="idx" cols="12" class="px-0 py-1">
                <v-row no-gutters>
                  <v-radio :label="item.text" :value="item.value">
                    <template #label>
                      <span class="radio--item text-body-2">{{ item.text }}</span>
                    </template>
                  </v-radio>
                </v-row>
                <v-row no-gutters v-if="item.hint" class="pl-8">
                  <span class="font-italic font-weight-light radio--hint">{{ item.hint }}</span>
                </v-row>
              </v-col>
            </template>
          </v-radio-group>
        </template>
        <template #geometry="scope">
          <sidebar-geometry-input v-bind="scope" v-model="printParams.geometry" :is-valid.sync="isGeometryValid" />
        </template>
        <template #north_arrow="scope">
          <data-input v-bind="scope" v-model="printParams['north_arrow']">
            <template #selection="{ item }">
              <img v-if="item.value" width="20px" height="20px" :src="getNorthArrowImage(item.value)" class="mr-1" />
              {{ item.text }}
            </template>
            <template #item="{ item }">
              <div :class="{ 'pl-7': !item.value }">
                <img v-if="item.value" width="20px" height="20px" :src="getNorthArrowImage(item.value)" class="mr-1" />
                {{ item.text }}
              </div>
            </template>
          </data-input>
        </template>
      </sidebar-form>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
export default {
  name: 'Prints',
  components: {
    DataInput,
    CardTitleRow: () => import('@/components/CardTitleRow'),
    SidebarForm: () => import('@/components/SidebarForm'),
    SidebarGeometryInput: () => import('@/components/SidebarGeometryInput'),
  },
  computed: {
    token: get('authentication/token'),
    mapCenter: get('map/center'),
    northArrows: get('admin/modulesMapping@printing.north_arrows'),
    northArrowItems() {
      const mappedNorthArrows = this.northArrows.map(northArrow => {
        return { text: northArrow.split('.')[0], value: northArrow };
      });
      mappedNorthArrows.unshift({ text: this.$i18n.t('sidebar.prints.none'), value: '' });
      return mappedNorthArrows;
    },
    inputs() {
      const inputs = [
        {
          name: 'printType',
          hasDivider: true,
          hasNameColumn: false,
          cols: '12',
          items: [
            { text: this.$i18n.t('sidebar.prints.singlePrint'), value: 'single' },
            {
              text: this.$i18n.t('sidebar.prints.serialPrint'),
              value: 'serial',
              hint: this.$i18n.t('sidebar.prints.serialPrintHint'),
            },
          ],
        },
        {
          name: 'paper_format',
          dataType: { name: 'select' },
          items: [
            { text: 'A4', value: 'a4' },
            { text: 'A3', value: 'a3' },
            { text: 'A2', value: 'a2' },
            { text: 'A1', value: 'a1' },
          ],
          translationPath: 'sidebar.prints.printFormat',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          disabledSorting: true,
        },
        {
          name: 'output_format',
          dataType: { name: 'select' },
          items: [
            { text: 'PDF', value: 'pdf' },
            { text: 'PNG', value: 'png' },
          ],
          translationPath: 'sidebar.prints.fileFormat',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
        },
        {
          name: 'orientation',
          dataType: { name: 'select' },
          items: [
            { text: this.$i18n.t('sidebar.prints.vertical'), value: 'vertical' },
            { text: this.$i18n.t('sidebar.prints.horizontal'), value: 'horizontal' },
          ],
          translationPath: 'sidebar.prints.orientation',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
        },
        {
          name: 'scale',
          dataType: { name: 'select' },
          items: [
            { text: '1:250', value: 250 },
            { text: '1:500', value: 500 },
            { text: '1:1000', value: 1000 },
            { text: '1:2000', value: 2000 },
            { text: '1:5000', value: 5000 },
            { text: '1:10000', value: 10000 },
          ],
          translationPath: 'sidebar.prints.scale',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          disabledSorting: true,
        },
        {
          name: 'header_position',
          dataType: { name: 'select' },
          items: [
            { text: this.$i18n.t('sidebar.prints.atTopOfPage'), value: 'top' },
            { text: this.$i18n.t('sidebar.prints.atBottomOfPage'), value: 'bottom' },
          ],
          translationPath: 'sidebar.prints.headerPosition',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
        },
        {
          name: 'north_arrow',
          dataType: { name: 'select' },
          items: this.northArrowItems,
          translationPath: 'sidebar.prints.northArrow',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          disabledSorting: true,
          oneLineInput: false,
          alignClass: 'center',
        },
        {
          name: 'map_rotation',
          dataType: { name: 'integer' },
          items: [],
          translationPath: 'sidebar.prints.mapRotation',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          allowNegative: false,
          maxValue: 360,
        },
        {
          name: 'show_legend',
          label: this.$i18n.t('sidebar.prints.showLegend'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
        {
          name: 'hide_footer',
          label: this.$i18n.t('sidebar.prints.showHideFooter'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
        {
          name: 'split',
          label: this.$i18n.t('sidebar.prints.split'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
      ];
      if (this.printType === 'serial') {
        const geometryItem = {
          name: 'geometry',
          dataType: { name: 'text' },
          hasNameColumn: false,
          icon: 'mdi-map-marker',
          geometryType: 'LineString',
          cols: '12',
          hint: this.$i18n.t('sidebar.nmtHint'),
        };
        inputs.splice(1, 0, geometryItem);
      }
      return inputs;
    },
    isOpenStreetMapVisible: get('map/isOpenStreetMapVisible'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    project: get('layers/project'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    isGeometryValid: true,
    printExtentCenter: [],
    printParams: {
      paper_format: 'a4',
      scale: 500,
      output_format: 'pdf',
      orientation: 'vertical',
      split: false,
      hide_footer: false,
      header_position: 'top',
      north_arrow: '',
      map_rotation: 0,
      show_legend: false,
    },
    printType: 'single',
    printingLayersAttributes: ['labels_visible', 'layer_id', 'line_direction', 'opacity', 'style', 'type', 'visible'],
    currentTaskId: null,
  }),
  methods: {
    mapPrint: call('map/mapPrint'),
    cancelMapPrint: call('celeryTasks/stopCeleryTask'),
    checkMapPrint: call('celeryTasks/getCeleryTaskStatus'),
    getMapPrint: call('celeryTasks/getCeleryTaskResult'),
    getNorthArrowImage(value) {
      return `${process.env.VUE_APP_API_URL}/settings/qgis_icons/${value}?token=${this.token}`;
    },
    updatePrintBox() {
      this.$root.$emit('updatePrintBox', this.printParams);
    },
    clearPrintBox() {
      this.$root.$emit('clearPrintBox');
    },
    updatePrintExtentCenter(center) {
      this.printExtentCenter = center;
    },
    async print() {
      this.isStickyButtonsLoading = true;
      const layers = [];
      for (const projectLayer of JSON.parse(JSON.stringify(this.$_getFlatGroupsLayers(this.project.layers).layers))) {
        for (const key of Object.keys(projectLayer)) {
          if (!this.printingLayersAttributes.includes(key)) {
            delete projectLayer[key];
          }
        }
        layers.push(projectLayer);
      }
      const centerCoords = this.printType === 'single' ? this.printExtentCenter : this.mapCenter;
      const centerCoordsGeojson = {
        type: 'Point',
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg || 'EPSG:4326',
          },
        },
        coordinates: centerCoords,
      };
      const payload = {
        ...{ ...this.printParams, hide_footer: !this.printParams.hide_footer },
        layers,
        map_center: centerCoordsGeojson,
      };
      if (this.isOpenStreetMapVisible) {
        payload.basemap = 'OpenStreetMap';
      }
      try {
        const taskId = await this.mapPrint(payload);
        this.currentTaskId = taskId;
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.generatingPrint'),
        });
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            try {
              const status = await this.checkMapPrint(taskId);
              if (['REVOKED', 'FAILURE'].includes(status)) {
                this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
                return 'cancel';
              }
              return status === 'SUCCESS';
            } catch (error) {
              this.currentTaskId = null;
              console.log('error', error);
            }
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            try {
              await this.getMapPrint({ id: taskId });
              this.currentTaskId = null;
              this.isStickyButtonsLoading = false;
            } catch (error) {
              this.currentTaskId = null;
              this.isStickyButtonsLoading = false;
              console.log('error', error);
            }
          },
          interval: 5000,
        });
      } catch (error) {
        this.currentTaskId = null;
        this.isStickyButtonsLoading = false;
        console.log('error', error);
      }
    },
  },
  watch: {
    isGeometryValid(nV) {
      this.isStickyButtonsValid = nV;
    },
    printType(nV) {
      if (nV !== 'serial') {
        this.updatePrintBox();
        delete this.printParams.geometry;
        this.isGeometryValid = true;
      } else {
        this.clearPrintBox();
        this.isGeometryValid = false;
      }
    },
  },
  mounted() {
    this.$root.$off('print');
    this.$root.$off('updatePrintExtentCenter');
    this.$root.$on('print', this.print);
    this.$root.$on('updatePrintExtentCenter', this.updatePrintExtentCenter);
    this.stickyButtons = [
      {
        action: 'print',
        translationPath: 'sidebar.prints.print',
      },
    ];
    this.isStickyButtonsVisible = true;
    this.isStickyButtonsValid = true;
    this.updatePrintBox();
    this.$watch(
      vm => {
        const { paper_format, scale, orientation, map_rotation } = vm.printParams;
        return [paper_format, scale, orientation, map_rotation];
      },
      () => {
        if (this.printType === 'single') {
          this.updatePrintBox();
        }
      }
    );
  },
  beforeDestroy() {
    if (this.currentTaskId) {
      this.cancelMapPrint(this.currentTaskId);
    }
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = false;
    this.clearPrintBox();
  },
};
</script>
<style lang="scss" scoped>
.radio--hint {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.59);
}
.radio--item {
  color: #202124;
}
::v-deep .v-input--checkbox .v-label {
  color: #202124;
}
</style>
